import { getCurrentDomain } from 'common/currentDomain';
import { ScheduleSendValues } from './types/scheduleSend';
import { AttachmentConfiguration, PdfFormat } from './types/attachment';
import { snakeCase } from 'lodash';
import {
  DayTypes,
  dayTypesDict,
  FrequencyConfig,
  IntervalTypes,
  intervalTypesDict,
  TimeInfo,
  timezoneDict
} from 'common/types/frequencySelector/frequencySelector';
import moment from 'moment-timezone';
import { mappedTimezone, reverseMappedTimezone } from '../FrequencySelector/helpers/timezoneHelper';
import { ScreenshotSubscription } from 'common/notifications/api/ScreenshotSubscriptionsAPI';
import { defaultDateFormat } from '../FrequencySelector/constants/dateFormats';
import { CatalogInteractiveUser } from 'common/types/users/catalogUsers';
import InteractiveUser from 'common/types/users/interactiveUser';

export const convertDateTimeToUTC = (date: string | undefined, timeInfo: TimeInfo) => {
  if (!date || !timeInfo || !timeInfo.time) return '';
  const dateTime = `${date} ${timeInfo.time}`;
  return moment.tz(dateTime, 'MM/DD/YYYY HH:mm', mappedTimezone(timeInfo.timeZone)).utc().format();
};

export const convertDays = (days: DayTypes[] | undefined) => {
  if (!days) return [];
  return days.map((day) => dayTypesDict[day].id).sort((a, b) => a - b);
};

export const buildFrequencyPayload = (config: FrequencyConfig) => {
  const { days: daysList, count, type, week } = config.intervalInfo;
  const { startDate, endDate, hasEndDate } = config.datesInfo;

  return {
    first_occurrence: convertDateTimeToUTC(startDate, config.timeInfo),
    first_occurrence_timezone: timezoneDict[config.timeInfo.timeZone],
    recurrence: {
      type: intervalTypesDict[type],
      interval: count,
      ...((type === 'week' || type === 'month') && { days: convertDays(daysList) }),
      ...(type === 'month' && {
        repeat_on: week
      })
    },
    last_occurrence: hasEndDate && endDate ? moment(new Date(endDate)).format('YYYY-MM-DD') : null
  };
};

export const buildPayload = (scheduledSendValues: ScheduleSendValues, currentUser: InteractiveUser) => {
  return {
    domain_cname: getCurrentDomain(),
    subscriber_uid: getRecipientsUid(scheduledSendValues),
    asset_uid: window.STORY_DATA.uid,
    schedule_name: scheduledSendValues.name,
    subject_line: scheduledSendValues.subject,
    subscribed_by_uid: currentUser.id,
    ...buildAttachmentPayload(scheduledSendValues.attachmentConfigs),
    ...buildFrequencyPayload(scheduledSendValues.frequencyConfig)
  };
};

export const convertCamelCasePdfOptionKeysToSnakeCase = (pdfOptions: PdfFormat) => {
  if (!pdfOptions) return undefined;

  const result = Object.entries(pdfOptions as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        const snakeKey = snakeCase(key);
        acc[snakeKey] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );

  return result;
};

const buildAttachmentPayload = (attachmentConfigs: AttachmentConfiguration[]) => {
  const attachments = attachmentConfigs.map((attachment) => {
    if (attachment.type === 'full_report') {
      return { type: attachment.type };
    } else {
      return {
        component_uid: attachment.componentUuid,
        type: attachment.type,
        format: attachment.format,
        name: attachment.name,
        ...(attachment.pdfOptions && {
          pdf_options: convertCamelCasePdfOptionKeysToSnakeCase(attachment.pdfOptions)
        })
      };
    }
  });

  return {
    attachments: attachments
  };
};

export const getRecipientsUid = (scheduledSendValues: ScheduleSendValues) => {
  return scheduledSendValues.recipients.map((recipient) => recipient.value);
};

export const buildFrequencyConfigFromSubscription = (subscription: ScreenshotSubscription) => {
  const dateTime = moment.tz(subscription.first_occurrence, subscription.first_occurrence_timezone || '');
  const startDate = dateTime.format(defaultDateFormat);
  const time = dateTime.format('HH:mm');
  return {
    intervalInfo: {
      count: subscription.interval.days,
      type: 'day' as IntervalTypes
    },
    timeInfo: {
      timeZone: reverseMappedTimezone(subscription.first_occurrence_timezone),
      time
    },
    datesInfo: {
      startDate,
      hasEndDate: false
    }
  };
};

export const buildRecipientsFromSubscriptions = (
  subscriptions: ScreenshotSubscription[],
  recipients: CatalogInteractiveUser[]
) => {
  return subscriptions.map((subscription) => ({
    label: recipients.find((recipient) => recipient.id === subscription.subscriber_uid)?.email || '',
    value: subscription.subscriber_uid
  }));
};
